import React, { ChangeEvent } from 'react';
import { useHistory } from 'react-router';
import { useDispatch } from 'react-redux';
import { Button, Form, FormGroup } from 'reactstrap';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import intl from 'react-intl-universal';

import Icon from 'components/Icon';
import Checkbox from 'components/Checkbox';
import Page from 'components/Page';
import PageHeader from 'components/PageHeader';
import PageFooter from 'components/PageFooter';
import FormBlock from 'modules/forms/components/FormBlock';
import { EAppointmentFormStatus, IForm } from 'modules/forms/interfaces/form.interfaces';
import { postAppointmentForm, setFormData } from 'modules/forms/actions/form.actions';
import { acuteSoreThroatRoutes } from 'modules/forms/constants/routes.constants';
import { useTypedSelector } from 'hooks/useTypedSelector';
import { getFormResultHTML } from 'modules/forms/helpers/formResult.helper';
import FormResult from './FormResult';

const FourthAssessment = (): JSX.Element => {
  const history = useHistory();
  const dispatch = useDispatch();
  const appointment = useTypedSelector(state => state.form.appointment);
  const stateForm = useTypedSelector(state => state.form.form);
  const methods = useForm<IForm>({
    defaultValues: {
      secondarySymptoms: stateForm.secondarySymptoms,
    },
    mode: 'onSubmit',
  });
  const { control, watch, setValue, handleSubmit } = methods;
  const secondarySymptoms = watch('secondarySymptoms') || [];

  const onBack = () => {
    history.goBack();
  };

  const onSubmit = (values: IForm) => {
    dispatch(setFormData(values));
    if (!values.secondarySymptoms?.includes('6')) {
      dispatch(
        postAppointmentForm({
          form: {
            appointmentUuid: appointment?.uuid,
            status: EAppointmentFormStatus.TO_GP,
            response: getFormResultHTML({ ...stateForm, ...values }, FormResult),
          },
          route: acuteSoreThroatRoutes.disqualification,
        }),
      );
    } else {
      history.push({
        pathname: acuteSoreThroatRoutes.fifthAssessment,
        search: history.location.search,
      });
    }
  };

  const onChangeValue = (name: string, e: ChangeEvent<HTMLInputElement>) => {
    setValue(
      'secondarySymptoms',
      e.target.checked
        ? [...secondarySymptoms, name].filter(item => item !== '6')
        : secondarySymptoms.filter(item => item !== name),
    );
  };

  return (
    <Page>
      <PageHeader step={6} maxSteps={8} />
      <FormProvider {...methods}>
        <Form className="page" onSubmit={handleSubmit(onSubmit)}>
          <div className="page-content">
            <main id="page-main-scrollable" className="page-main">
              <FormBlock
                title="Assessment"
                description="Which of the following symptoms is the patient experiencing (select all that apply):"
              >
                <Controller
                  name="secondarySymptoms"
                  control={control}
                  render={() => (
                    <FormGroup>
                      <Checkbox
                        id="1"
                        name="1"
                        checked={secondarySymptoms?.includes('1')}
                        onChange={e => onChangeValue('1', e)}
                        label="The patient recently displayed a severe red rash on their skin, which looks like small raised bumps and makes the skin feel like sandpaper."
                      />
                      <Checkbox
                        id="2"
                        name="2"
                        checked={secondarySymptoms?.includes('2')}
                        onChange={e => onChangeValue('2', e)}
                        label="The patient is feeling extremely tired and weak."
                      />
                      <Checkbox
                        id="3"
                        name="3"
                        checked={secondarySymptoms?.includes('3')}
                        onChange={e => onChangeValue('3', e)}
                        label="The patient has had a sore throat or had difficulty swallowing for a few weeks or longer."
                      />
                      <Checkbox
                        id="4"
                        name="4"
                        checked={secondarySymptoms?.includes('4')}
                        onChange={e => onChangeValue('4', e)}
                        label="The patient's voice changed over the last few weeks sounding different or hoarse."
                      />
                      <Checkbox
                        id="5"
                        name="5"
                        checked={secondarySymptoms?.includes('5')}
                        onChange={e => onChangeValue('5', e)}
                        label="The patient lost weight over the past few months (Unexplained weight loss)."
                      />
                      <Checkbox
                        id="6"
                        name="6"
                        checked={secondarySymptoms?.includes('6')}
                        onChange={e => setValue('secondarySymptoms', e.target.checked ? ['6'] : [])}
                        label="None of the Above"
                      />
                    </FormGroup>
                  )}
                />
              </FormBlock>
            </main>

            <PageFooter>
              <Button color="primary" outline type="button" block onClick={onBack}>
                <Icon iconName="arrow-left" />
                {intl.get('button.back')}
              </Button>
              <Button
                color="primary"
                type="button"
                block
                disabled={!secondarySymptoms?.length}
                onClick={handleSubmit(onSubmit)}
              >
                {intl.get('button.next')} <Icon iconName="arrow-right" />
              </Button>
            </PageFooter>
          </div>
        </Form>
      </FormProvider>
    </Page>
  );
};

export default FourthAssessment;
